import React, { useState, useRef, useEffect } from 'react';
import { useTonConnectUI, useTonAddress, useTonWallet } from '@tonconnect/ui-react';
import { useWalletStore } from '../store/wallet';
import { useWalletConnect } from '../hooks/useWalletConnect';
import { useTelegramApp } from '../hooks/useTelegramApp';
import { CoinIcon, LinkIcon } from './icons/WeatherIcons';
import { formatTON, abbreviateAddress } from '../utils/tonMessages';
import { TON_EXPLORER_BASE } from '../config/contracts';
import './WalletButton.css';

type ModalView = 'closed' | 'select' | 'wc-qr' | 'connected-menu';

const WalletButton: React.FC = () => {
  const [modalView, setModalView] = useState<ModalView>('closed');
  const [copied, setCopied] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [tonConnectUI] = useTonConnectUI();
  const tonAddress = useTonAddress();
  const tonWallet = useTonWallet();
  const { hapticFeedback } = useTelegramApp();

  const {
    address: storeAddress,
    walletType,
    balances,
    setAddress,
    disconnect: storeDisconnect,
  } = useWalletStore();

  const {
    uri: wcUri,
    isConnecting: wcConnecting,
    connect: wcConnect,
    disconnect: wcDisconnect,
  } = useWalletConnect();

  // Sync TonConnect wallet into our store
  useEffect(() => {
    if (tonAddress && tonWallet && walletType !== 'walletconnect') {
      if (storeAddress !== tonAddress) {
        setAddress(tonAddress, 'tonconnect');
      }
    } else if (!tonWallet && walletType === 'tonconnect') {
      storeDisconnect();
    }
  }, [tonAddress, tonWallet, walletType, storeAddress, setAddress, storeDisconnect]);

  // Close dropdown on outside click
  useEffect(() => {
    if (modalView !== 'connected-menu') return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setModalView('closed');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [modalView]);

  const isConnected = !!storeAddress;
  const displayAddress = storeAddress || '';
  const tonBalanceStr = formatTON(balances.ton, 3);

  const handleConnectTonConnect = async () => {
    hapticFeedback('light');
    setModalView('closed');
    await tonConnectUI.openModal();
  };

  const handleConnectWalletConnect = async () => {
    hapticFeedback('light');
    setModalView('wc-qr');
    await wcConnect();
    if (useWalletStore.getState().isConnected) {
      setModalView('closed');
    }
  };

  const handleDisconnect = async () => {
    hapticFeedback('light');
    setModalView('closed');
    if (walletType === 'walletconnect') {
      await wcDisconnect();
    } else {
      await tonConnectUI.disconnect();
      storeDisconnect();
    }
  };

  const handleCopyAddress = () => {
    if (!displayAddress) return;
    navigator.clipboard.writeText(displayAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    hapticFeedback('light');
  };

  if (isConnected) {
    const shortAddress = abbreviateAddress(displayAddress, 4);
    const connectionLabel = walletType === 'walletconnect' ? 'WC' : 'TC';
    const connectionTitle = walletType === 'walletconnect' ? 'WalletConnect' : 'TonConnect';

    return (
      <div className="wallet-button-container" ref={dropdownRef}>
        <button
          className="wallet-button connected"
          onClick={() => setModalView(modalView === 'connected-menu' ? 'closed' : 'connected-menu')}
        >
          <span className="wallet-indicator" title={connectionTitle} />
          <span className="wallet-address">{shortAddress}</span>
          <span className="wallet-balance">{tonBalanceStr} TON</span>
          <span className="wallet-connection-badge">{connectionLabel}</span>
          <span className="wallet-chevron">▾</span>
        </button>

        {modalView === 'connected-menu' && (
          <div className="wallet-dropdown">
            <div className="wallet-dropdown-header">
              <span className="wallet-dropdown-addr">{abbreviateAddress(displayAddress, 6)}</span>
              <span className={`wallet-dropdown-type ${walletType}`}>
                {walletType === 'walletconnect' ? 'WalletConnect' : 'TonConnect'}
              </span>
            </div>
            <div className="wallet-dropdown-balance">
              <div className="wdb-row">
                <span>Wallet</span>
                <span>{tonBalanceStr} TON</span>
              </div>
              <div className="wdb-row">
                <span>Margin</span>
                <span className="accent">{formatTON(balances.marginAvailable, 3)} TON</span>
              </div>
            </div>
            <div className="wallet-dropdown-actions">
              <button className="wda-btn" onClick={handleCopyAddress}>
                {copied ? '✅ Copied!' : '📋 Copy Address'}
              </button>
              <a
                className="wda-btn"
                href={`${TON_EXPLORER_BASE}/address/${displayAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setModalView('closed')}
              >
                🔍 View on Explorer ↗
              </a>
              <button className="wda-btn disconnect" onClick={handleDisconnect}>
                🔌 Disconnect
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wallet-button-container">
      {modalView === 'closed' && (
        <button
          className="wallet-button"
          onClick={() => { hapticFeedback('light'); setModalView('select'); }}
        >
          Connect Wallet
        </button>
      )}

      {modalView === 'select' && (
        <div className="wallet-modal-overlay" onClick={() => setModalView('closed')}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-modal-header">
              <span>Connect Wallet</span>
              <button className="wallet-modal-close" onClick={() => setModalView('closed')}>×</button>
            </div>
            <div className="wallet-modal-options">
              <button className="wallet-option-btn tonconnect" onClick={handleConnectTonConnect}>
                <div className="wob-icon"><CoinIcon size={24} /></div>
                <div className="wob-info">
                  <span className="wob-title">TonConnect</span>
                  <span className="wob-desc">Tonkeeper, MyTonWallet, Telegram Wallet</span>
                </div>
                <span className="wob-arrow">›</span>
              </button>

              <button
                className="wallet-option-btn walletconnect"
                onClick={handleConnectWalletConnect}
                disabled={wcConnecting}
              >
                <div className="wob-icon"><LinkIcon size={24} /></div>
                <div className="wob-info">
                  <span className="wob-title">WalletConnect</span>
                  <span className="wob-desc">Ledger, Safe, institutional wallets</span>
                </div>
                {wcConnecting ? (
                  <span className="wob-spinner" />
                ) : (
                  <span className="wob-arrow">›</span>
                )}
              </button>
            </div>
            <p className="wallet-modal-note">
              New to TON? Get testnet tokens from the{' '}
              <a href="https://t.me/testgiver_ton_bot" target="_blank" rel="noopener noreferrer">
                faucet ↗
              </a>
            </p>
          </div>
        </div>
      )}

      {modalView === 'wc-qr' && (
        <div className="wallet-modal-overlay" onClick={() => setModalView('closed')}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-modal-header">
              <button className="wallet-modal-back" onClick={() => setModalView('select')}>‹</button>
              <span>WalletConnect</span>
              <button className="wallet-modal-close" onClick={() => setModalView('closed')}>×</button>
            </div>

            {wcConnecting && !wcUri && (
              <div className="wc-connecting">
                <span className="wc-spinner" />
                <span>Generating pairing URI...</span>
              </div>
            )}

            {wcUri && (
              <div className="wc-qr-section">
                <p className="wc-instruction">
                  Scan with your institutional wallet (Ledger Live, Safe, etc.)
                </p>
                <WcQrCode uri={wcUri} />
                <div className="wc-uri-copy">
                  <code className="wc-uri-text">{wcUri.slice(0, 40)}...</code>
                  <button
                    className="wc-copy-btn"
                    onClick={() => navigator.clipboard.writeText(wcUri)}
                  >
                    Copy URI
                  </button>
                </div>
              </div>
            )}

            {!wcConnecting && !wcUri && (
              <div className="wc-connecting">
                <span>Waiting for wallet approval...</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Simple QR code renderer using a data URI from qrserver.com (no deps)
const WcQrCode: React.FC<{ uri: string }> = ({ uri }) => {
  const encoded = encodeURIComponent(uri);
  const src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encoded}&bgcolor=1a1a2e&color=00F0FF&margin=10`;
  return (
    <div className="wc-qr-wrapper">
      <img src={src} alt="WalletConnect QR Code" width={200} height={200} />
    </div>
  );
};

export default WalletButton;
